import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Briefcase, CreditCard, Settings, Home, LogOut, ChevronLeft, ChevronRight, ChevronDown, CalendarDays, Users, FileText, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Spinner from './Spinner';
import strivreLogo from '../assets/images/strivre-logo.svg';
import { PlatformAddOn } from '../types/userManagement';

interface NavItem {
  name: string;
  icon: React.ReactNode;
  path?: string;

  children?: {
    name: string;
    path: string;
    adminOnly?: boolean;
  }[];

  adminOnly?: boolean;

  requiredAddOn?: PlatformAddOn;
}

const navItems: NavItem[] = [
  { name: 'Home', icon: <Home size={20} />, path: '/' },
  {
    name: 'Diagnostics',
    icon: <Search size={20} />,
    adminOnly: true,
    children: [
      {
        name: 'Overview',
        path: '/diagnostics/overview',
      },
      {
        name: 'Website Diagnostic Tool',
        path: '/diagnostics/website-tool',
      },
      {
        name: 'Marketing Diagnostic Tool',
        path: '/diagnostics/marketing-tool',
      },
      {
        name: 'Business Health & Strategy Assessment',
        path: '/diagnostics/business-assessment',
      },
      {
        name: 'Talent Acquisition Diagnostics',
        path: '/diagnostics/talent-diagnostics',
      },
      {
        name: 'Recruitment Diagnostics',
        path: '/diagnostics/recruitment-diagnostics',
      },
      {
        name: 'Diagnostic Leads',
        path: '/diagnostics/leads',
      },
    ],
  },

  // { name: 'Website', icon: <Layers size={20} />, path: '/foundation' },
  {
    name: 'Job Board',
    icon: <Briefcase size={20} />,
    requiredAddOn: PlatformAddOn.JobBoard,
    children: [
    { name: 'Dashboard', path: '/job-board/dashboard' },
    { name: 'Jobs', path: '/job-board/jobs' },
    { name: 'Applicants', path: '/job-board/applicants' },
    { name: 'Configuration', path: '/job-board/settings' },
    ],
  },
  {
    name: 'Billing & Proposals',
    icon: <CreditCard size={20} />,
    requiredAddOn: PlatformAddOn.BillingAndProposals,
    children: [
      { name: 'Invoices', path: '/invoices' },
      { name: 'Proposals', path: '/proposals' },
    ],
  },
  {
    name: 'Booking & Scheduling',
    icon: <CalendarDays size={20} />,
    requiredAddOn: PlatformAddOn.BookingAndScheduling,
    children: [
      { name: 'Dashboard', path: '/booking/dashboard' },
      { name: 'Services', path: '/booking/services' },
      { name: 'Availability', path: '/booking/availability' },
      { name: 'Bookings', path: '/booking/bookings' },
      { name: 'Reports', path: '/booking/reports' },
      { name: 'Configuration', path: '/booking/configuration' },
    ],
  },
  {
    name: 'Forms',
    icon: <FileText size={20} />,
    requiredAddOn: PlatformAddOn.Forms,
    children: [
      { name: 'Dashboard', path: '/forms/dashboard' },
      { name: 'Forms', path: '/forms' },
    ],
  },
  {
    name: 'User Management',
    icon: <Users size={20} />,
    adminOnly: true,
    children: [
      { name: 'Users', path: '/user-management' },
    ],
  },
  { name: 'Settings', icon: <Settings size={20} />, path: '/settings' },
];

export default function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false); // Default collapsed on small screens
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [lockedModule, setLockedModule] = useState<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAdmin, hasAddOn, user } = useAuth();
  const visibleNavItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  const userInitials = user
    ? `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`.toUpperCase()
    : '';
  
  const userDisplayName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
    : '';

  const isActive = (path: string) => {
    const pathname = location.pathname;

    if (path === '/forms') {
      return pathname === '/forms';
    }

    return pathname === path || pathname.startsWith(`${path}/`);
  };
  useEffect(() => {
    // Check for token in localStorage to verify session
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const activeParent = navItems.find(
      item =>
        item.children?.some(child =>
          location.pathname.startsWith(child.path)
        )
    );

    if (activeParent) {
      setOpenAccordion(activeParent.name);
    }
  }, [location.pathname]);

  const toggleAccordion = (name: string) => {
    // If sidebar is collapsed, expand it when opening an accordion
    if (isCollapsed) {
      setIsCollapsed(false);
      setOpenAccordion(name);
      return;
    }
    setOpenAccordion(openAccordion === name ? null : name);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      {/* Mobile Hamburger Trigger - Only visible when sidebar is hidden */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="md:hidden fixed top-4 left-4 z-50 p-2.5 bg-white border border-strivre-lightnavy shadow-lg rounded-xl text-slate-600 hover:text-strivre-burgundy transition-all active:scale-95"
          aria-label="Open sidebar"
        >
          <Menu size={24} />
        </button>
      )}

      {/* Sidebar Aside */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-strivre-navy text-white flex flex-col transition-all duration-300 ease-in-out md:sticky md:top-0 md:h-screen ${
          isCollapsed 
            ? '-translate-x-full md:translate-x-0 md:w-20' 
            : 'translate-x-0 w-64 shadow-2xl md:shadow-none'
        }`}
      >
        {/* Header: Logo and Toggle Buttons */}
        <div
          className={`border-b border-white/10 flex-shrink-0 ${
            isCollapsed
              ? 'py-3 flex flex-col items-center gap-1'
              : 'h-28 flex items-center justify-between px-4'
          }`}
        >
          {!isCollapsed && (
            <div className="flex-1 min-w-0 pr-3 animate-fade-in">
              <div className="bg-white rounded-xl px-2">
                <img
                  src={strivreLogo}
                  alt="Strivre"
                  className="h-20 max-w-[180px] w-auto object-contain"
                  />
              </div>
            </div>
          )}

          {isCollapsed && (
            <div className="flex items-center justify-center">
              <div
                className="
                  w-10 h-10
                  rounded-lg
                  bg-strivre-burgundy
                  text-white
                  font-bold
                  text-xl
                  flex items-center justify-center
                  shadow-md
                "
              >
                S
              </div>
            </div>
          )}

          {/* Collapse/Expand for Desktop */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="
              hidden md:flex
              p-2
              rounded-lg
              text-strivre-lightnavy
              hover:bg-strivre-burgundy
              hover:text-white
              transition-all duration-200
              focus:outline-none
              focus:ring-2
              focus:ring-strivre-burgundy
            "
            aria-label="Toggle sidebar width"
          >
            {isCollapsed ? (
              <ChevronRight size={18} />
            ) : (
              <ChevronLeft size={18} />
            )}
          </button>

          {/* Close for Mobile */}
          <button
            onClick={() => setIsCollapsed(true)}
            className="
              md:hidden
              p-2
              hover:bg-strivre-lightnavy
              text-strivre-lightnavy
              hover:text-white
              rounded-lg
              transition-all duration-200
            "
            aria-label="Close sidebar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 mt-4 px-3 space-y-1.5 overflow-y-auto scrollbar-hide">
          {visibleNavItems.map((item) => {
            const isLocked =
              item.requiredAddOn !== undefined &&
              !hasAddOn(item.requiredAddOn);

            // Items with accordion children
            if (item.children) {
              return (
                <div key={item.name}>
                  <button
                    type="button"
                    onClick={() => {
                      if (isLocked) {
                        setLockedModule(item.name);
                        return;
                      }

                      toggleAccordion(item.name);
                    }}
                    // disabled={isLocked}
                    title={
                      isLocked
                        ? `${item.name} - Subscription required`
                        : undefined
                    }
                    className={`flex items-center gap-4 px-4 py-3 w-full rounded-lg transition-all duration-200 group text-left ${
                      isLocked
                        ? 'cursor-pointer text-strivre-lightnavy/50 hover:bg-white/5 hover:text-strivre-lightnavy/70'
                        : openAccordion === item.name
                          ? 'bg-strivre-burgundy text-white shadow-lg shadow-strivre-burgundy/40'
                          : 'text-strivre-lightnavy hover:bg-strivre-burgundy/80 hover:text-white'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <span
                        className="text-current"
                        aria-hidden="true"
                      >
                        {item.icon}
                      </span>

                      {/* Tooltip when sidebar is collapsed */}
                      {isCollapsed && (
                        <span
                          className="
                            absolute left-12 top-1/2 -translate-y-1/2
                            px-3 py-1.5
                            rounded-md
                            bg-strivre-burgundy
                            text-white
                            text-sm
                            whitespace-nowrap
                            opacity-0
                            invisible
                            group-hover:opacity-100
                            group-hover:visible
                            transition-all
                            duration-200
                            z-50
                            shadow-lg
                          "
                        >
                          {item.name}
                          {isLocked ? ' - Subscription required' : ''}
                        </span>
                      )}

                      {/* Small lock indicator when collapsed */}
                      {isCollapsed && isLocked && (
                        <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-strivre-burgundy text-white">
                          <Lock size={9} />
                        </span>
                      )}
                    </div>

                    {!isCollapsed && (
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <span className="font-medium whitespace-nowrap">
                          {item.name}
                        </span>

                        {isLocked ? (
                          <Lock
                            size={15}
                            className="shrink-0 opacity-70"
                          />
                        ) : (
                          <ChevronDown
                            size={16}
                            className={`shrink-0 transition-transform duration-200 ${
                              openAccordion === item.name
                                ? 'rotate-180'
                                : ''
                            }`}
                          />
                        )}
                      </div>
                    )}
                  </button>

                  {/* Accordion children */}
                  {!isLocked &&
                    openAccordion === item.name &&
                    !isCollapsed && (
                      <div className="ml-10 mt-2 space-y-1 text-left">
                        {item.children
                          .filter((child) => !child.adminOnly || isAdmin)
                          .map((child) => (
                            <Link
                              key={child.name}
                              to={child.path}
                              className={`block px-2 py-2 rounded-lg text-sm transition-colors ${
                                isActive(child.path)
                                  ? 'bg-strivre-burgundy text-white'
                                  : 'text-strivre-lightnavy hover:bg-strivre-burgundy/80 hover:text-white'
                              }`}
                            >
                              {child.name}
                            </Link>
                          ))}
                      </div>
                    )}
                </div>
              );
            }

            // Locked items without children
            if (isLocked) {
              return (
                <button
                  type="button"
                  key={item.name}
                  title={`${item.name} - Subscription required`}
                  onClick={() => setLockedModule(item.name)}
                  className="group flex w-full cursor-pointer items-center gap-4 rounded-lg px-4 py-3 text-left text-strivre-lightnavy/50 transition-all duration-200 hover:bg-white/5 hover:text-strivre-lightnavy/70"
                >
                  <div className="relative flex-shrink-0">
                    <span aria-hidden="true">
                      {item.icon}
                    </span>

                    {isCollapsed && (
                      <>
                        <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-strivre-burgundy text-white">
                          <Lock size={9} />
                        </span>

                        <span
                          className="
                            absolute left-12 top-1/2 -translate-y-1/2
                            px-3 py-1.5
                            rounded-md
                            bg-strivre-burgundy
                            text-white
                            text-sm
                            whitespace-nowrap
                            opacity-0
                            invisible
                            group-hover:opacity-100
                            group-hover:visible
                            transition-all
                            duration-200
                            z-50
                            shadow-lg
                          "
                        >
                          {item.name} - Subscription required
                        </span>
                      </>
                    )}
                  </div>

                  {!isCollapsed && (
                    <>
                      <span className="flex-1 font-medium whitespace-nowrap">
                        {item.name}
                      </span>

                      <Lock
                        size={15}
                        className="shrink-0 opacity-70"
                      />
                    </>
                  )}
                </button>
              );
            }

            // Normal navigation items without children
            return (
              <Link
                key={item.name}
                to={item.path!}
                className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-all duration-200 group ${
                  isActive(item.path!)
                    ? 'bg-strivre-burgundy text-white shadow-lg shadow-strivre-burgundy/40'
                    : 'text-strivre-lightnavy hover:bg-strivre-burgundy/80 hover:text-white'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <span
                    className="text-current"
                    aria-hidden="true"
                  >
                    {item.icon}
                  </span>

                  {isCollapsed && (
                    <span
                      className="
                        absolute left-12 top-1/2 -translate-y-1/2
                        px-3 py-1.5
                        rounded-md
                        bg-strivre-burgundy
                        text-white
                        text-sm
                        whitespace-nowrap
                        opacity-0
                        invisible
                        group-hover:opacity-100
                        group-hover:visible
                        transition-all
                        duration-200
                        z-50
                        shadow-lg
                      "
                    >
                      {item.name}
                    </span>
                  )}
                </div>

                {!isCollapsed && (
                  <span className="font-medium whitespace-nowrap">
                    {item.name}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Logout Button */}
          {/* <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="
              flex items-center gap-4 px-4 py-3 w-full rounded-lg
              transition-all duration-200
              text-strivre-lightburgundy
              hover:bg-strivre-burgundy
              hover:text-white
              hover:shadow-md
              hover:shadow-strivre-burgundy/30
              disabled:opacity-50
              group
            "
            aria-label="Log out"
          >
            <span className="flex-shrink-0" title={isCollapsed ? 'Logout' : undefined}>
              {isLoggingOut ? (
                <Spinner size="h-5 w-5" className="text-current" />
              ) : (
                <LogOut size={20} />
              )}
            </span>

            {!isCollapsed && (
              <span className="font-medium whitespace-nowrap">
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </span>
            )}
          </button> */}
        </nav>
        {/* Account Section */}
        <div
          className={`shrink-0 border-t border-white/10 ${
            isCollapsed ? 'px-3 py-3' : 'px-3 py-4'
          }`}
        >
          {/* Current User */}
          {user && (
            <div
              className={`mb-2 flex items-center rounded-lg ${
                isCollapsed
                  ? 'justify-center px-0 py-2'
                  : 'gap-3 px-3 py-2'
              }`}
              title={
                isCollapsed
                  ? `${userDisplayName}${isAdmin ? ' - Administrator' : ''}`
                  : undefined
              }
            >
              {/* Avatar */}
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-strivre-burgundy text-sm font-bold text-white shadow-sm">
                {userInitials || 'U'}
              </div>

              {/* User information */}
              {!isCollapsed && (
                <div className="min-w-0 flex-1 text-left">
                  <p className="truncate text-sm font-semibold text-white">
                    {userDisplayName || 'User'}
                  </p>

                  <p className="truncate text-xs text-strivre-lightnavy">
                    {isAdmin ? 'Administrator' : 'User'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Logout */}
          <button
            type="button"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`group flex w-full items-center rounded-lg text-strivre-lightburgundy transition-all duration-200 hover:bg-strivre-burgundy hover:text-white hover:shadow-md hover:shadow-strivre-burgundy/30 disabled:opacity-50 ${
              isCollapsed
                ? 'justify-center px-2 py-3'
                : 'gap-4 px-4 py-3'
            }`}
            aria-label="Log out"
            title={isCollapsed ? 'Logout' : undefined}
          >
            <span className="shrink-0">
              {isLoggingOut ? (
                <Spinner
                  size="h-5 w-5"
                  className="text-current"
                />
              ) : (
                <LogOut size={20} />
              )}
            </span>

            {!isCollapsed && (
              <span className="font-medium whitespace-nowrap">
                {isLoggingOut
                  ? 'Logging out...'
                  : 'Logout'}
              </span>
            )}
          </button>
        </div>

        {/* Footer */}
        {!isCollapsed && (
          <div className="shrink-0 border-t border-white/10 p-4 text-xs text-strivre-lightnavy">
            <p>© 2026 Strivre</p>
            <p className="mt-1">v1.0.0</p>
          </div>
        )}
      </aside>

      {/* Subscription Upgrade Modal */}
      {lockedModule && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm"
          onClick={() => setLockedModule(null)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Close */}
            <button
              type="button"
              onClick={() => setLockedModule(null)}
              aria-label="Close"
              className="absolute right-4 top-4 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-strivre-burgundy"
            >
              <X size={18} />
            </button>

            {/* Lock */}
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-strivre-lightburgundy">
              <Lock
                size={26}
                className="text-strivre-burgundy"
              />
            </div>

            <h2 className="mt-5 text-xl font-bold text-strivre-navy">
              Unlock {lockedModule}
            </h2>

            <p className="mt-3 text-sm leading-6 text-strivre-mutedtext">
              This feature isn't included in your current subscription.
              Contact our sales team to learn more about adding{' '}
              <span className="font-semibold text-strivre-darktext">
                {lockedModule}
              </span>{' '}
              to your account.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setLockedModule(null)}
                className="btn-secondary btn-md flex-1"
              >
                Maybe Later
              </button>

              <a
                href="https://strivreservices.com/contact-us/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-md flex flex-1 items-center justify-center"
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Overlay for Mobile */}
      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 md:hidden z-30"
          onClick={() => setIsCollapsed(true)}
        />
      )}
    </>
  );
}
